package org.catharsis.spendtel.feature.presentation

import org.catharsis.spendtel.core.api.AnalyticsApi
import org.catharsis.spendtel.core.api.AuthenticationRepo
import org.catharsis.spendtel.core.api.ExpenseReportRepo
import org.catharsis.spendtel.core.arch.MviViewModel
import org.catharsis.spendtel.core.arch.ViewEffect
import org.catharsis.spendtel.core.arch.ViewEvent
import org.catharsis.spendtel.core.arch.ViewState
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.EventName
import org.catharsis.spendtel.feature.share.ShareCardContent
import org.catharsis.spendtel.feature.share.ShareImageCard
import org.catharsis.spendtel.feature.share.canShareSummary
import org.catharsis.spendtel.feature.share.publicShareUrl
import org.catharsis.spendtel.feature.share.shareTitle
import org.catharsis.spendtel.feature.share.toShareSummary

class ExpenseReportViewModel(
    private val reportId: String,
    private val authenticationRepo: AuthenticationRepo,
    private val expenseReportRepo: ExpenseReportRepo,
    private val analytics: AnalyticsApi,
) : MviViewModel<ExpenseReportEvent, ExpenseReportState, ExpenseReportEffect>(
    initialState = ExpenseReportState(reportId = reportId),
) {
    init {
        launch { listenToReport() }
    }

    override fun receiveEvent(event: ExpenseReportEvent) {
        when (event) {
            ExpenseReportEvent.OnRefresh -> launch { listenToReport() }
            is ExpenseReportEvent.OnShareSummary -> {
                if (!event.report.canShareSummary()) return
                analytics.logClickEvent(
                    EventName.SHARE_SUMMARY,
                    mapOf("report_id" to event.report.id),
                )
                emitEffect {
                    ExpenseReportEffect.Share(
                        title = event.report.shareTitle(),
                        text = event.report.toShareSummary(),
                    )
                }
            }
            is ExpenseReportEvent.OnShareCard -> {
                analytics.logClickEvent(
                    EventName.SHARE_CARD,
                    mapOf(
                        "report_id" to reportId,
                        "card_type" to event.content.title,
                    ),
                )
                emitEffect {
                    ExpenseReportEffect.Share(
                        title = event.content.title,
                        text = event.content.text,
                    )
                }
            }
            is ExpenseReportEvent.OnShareImageCard -> {
                analytics.logClickEvent(
                    EventName.SHARE_IMAGE,
                    mapOf(
                        "report_id" to reportId,
                        "card_type" to shareImageCardType(event.card),
                    ),
                )
                emitEffect { ExpenseReportEffect.ShareImage(event.card) }
            }
            is ExpenseReportEvent.OnCreatePublicLink -> launch { createPublicLink(event.report) }
        }
    }

    private suspend fun createPublicLink(report: ExpenseReport) {
        if (!report.canShareSummary()) return

        val userId = authenticationRepo.getCurrentUser()?.uid
        if (userId.isNullOrBlank()) {
            emitEffect { ExpenseReportEffect.ShareFailed("You must be signed in to create a share link.") }
            return
        }

        setState { copy(isCreatingShareLink = true) }
        val result = expenseReportRepo.createPublicShare(userId, report)
        setState { copy(isCreatingShareLink = false) }

        result.fold(
            onSuccess = { shareId ->
                analytics.logClickEvent(
                    EventName.SHARE_LINK_CREATED,
                    mapOf("report_id" to report.id, "share_id" to shareId),
                )
                emitEffect { ExpenseReportEffect.ShareLink(publicShareUrl(shareId)) }
            },
            onFailure = { error ->
                analytics.logClickEvent(
                    EventName.SHARE_FAILED,
                    mapOf(
                        "report_id" to report.id,
                        "reason" to (error.message ?: "unknown"),
                    ),
                )
                emitEffect {
                    ExpenseReportEffect.ShareFailed(
                        error.message ?: "Could not create a share link.",
                    )
                }
            },
        )
    }

    private suspend fun listenToReport() {
        setState { copy(isLoading = true, error = null) }

        val userId = authenticationRepo.getCurrentUser()?.uid
        if (userId.isNullOrBlank()) {
            setState {
                copy(
                    isLoading = false,
                    error = "You must be signed in to view this report.",
                )
            }
            return
        }

        expenseReportRepo.listenToExpenseReport(userId, reportId).collect { report ->
            setState {
                copy(
                    isLoading = false,
                    report = report,
                    error = if (report == null) "This expense report could not be found." else null,
                )
            }
        }
    }
}

private fun shareImageCardType(card: ShareImageCard): String = when (card) {
    is ShareImageCard.Highlight -> "highlight"
    is ShareImageCard.Equivalence -> "equivalence"
    is ShareImageCard.Personality -> "personality"
}

data class ExpenseReportState(
    val reportId: String,
    val isLoading: Boolean = true,
    val report: ExpenseReport? = null,
    val error: String? = null,
    val isCreatingShareLink: Boolean = false,
) : ViewState {
    val isProcessing: Boolean
        get() = report?.isProcessing == true
}

sealed class ExpenseReportEvent : ViewEvent {
    data object OnRefresh : ExpenseReportEvent()
    data class OnShareSummary(val report: ExpenseReport) : ExpenseReportEvent()
    data class OnShareCard(val content: ShareCardContent) : ExpenseReportEvent()
    data class OnShareImageCard(val card: ShareImageCard) : ExpenseReportEvent()
    data class OnCreatePublicLink(val report: ExpenseReport) : ExpenseReportEvent()
}

sealed class ExpenseReportEffect : ViewEffect {
    data class Share(val title: String, val text: String) : ExpenseReportEffect()
    data class ShareImage(val card: ShareImageCard) : ExpenseReportEffect()
    data class ShareLink(val url: String) : ExpenseReportEffect()
    data class ShareFailed(val message: String) : ExpenseReportEffect()
}
